import { X } from 'lucide-react';
import { Badge } from '../common';
import { formatDateTimeWithSeconds } from '../../utils/dateTime';
import { isPerPalletClient } from '../../utils/clientTypeHelpers';

interface ReportDetailDrawerProps {
  reportType: string;
  row: any;
  onClose: () => void;
}

export default function ReportDetailDrawer({ reportType, row, onClose }: ReportDetailDrawerProps) {

  const renderDetails = () => {
    switch (reportType) {
      case 'inventory':
        return (
          <>
            <DetailRow label="Client" value={row.clientName} />
            <DetailRow label="Product" value={row.productDescription} />
            <DetailRow label="Batch No" value={row.batchNo} />
            <DetailRow label="Good Quantity" value={row.goodQty?.toLocaleString() || '0'} />
            <DetailRow label="CDAM Quantity" value={row.cdamQty?.toLocaleString() || '0'} />
            <DetailRow label="DAM Quantity" value={row.damQty?.toLocaleString() || '0'} />
            <DetailRow label="Total Pallets" value={row.palletCount?.toLocaleString() || '0'} />
            {row.storageLocations && (
              <DetailRow 
                label="Storage Locations" 
                value={
                  <div className="space-y-1">
                    {row.storageLocations.split(' | ').map((loc: string, idx: number) => (
                      <div key={idx} className="px-2 py-1 bg-brand-50 text-brand-700 rounded text-sm">
                        {loc}
                      </div>
                    ))}
                  </div>
                } 
              />
            )}
            <DetailRow label="Last Updated" value={formatDateTimeWithSeconds(row.updatedAt)} />
          </>
        );

      case 'inward':
        return (
          <>
            <DetailRow label="GRN Number" value={row.grnNumber} />
            <DetailRow label="Gate Entry" value={row.gateEntryNumber} />
            <DetailRow label="Client" value={row.clientName} />
            <DetailRow label="Warehouse" value={row.warehouseName} />
            <DetailRow label="Product" value={row.productDescription} />
            <DetailRow label="Batch No" value={row.batchNo} />
            <DetailRow label="Handling Type" value={row.handlingType ? row.handlingType.toUpperCase() : '-'} />
            <DetailRow label="Good Quantity" value={row.goodQty?.toLocaleString() || '0'} />
            <DetailRow label="CDAM Quantity" value={row.cdamQty?.toLocaleString() || '0'} />
            <DetailRow label="DAM Quantity" value={row.damQty?.toLocaleString() || '0'} />
            <DetailRow label="Total Pallets" value={row.palletCount?.toLocaleString() || '0'} />
            <DetailRow label="Total Weight" value={row.totalWeight ? `${row.totalWeight} kg` : '-'} />
            <DetailRow 
              label="Status" 
              value={<Badge variant={row.grnStatus === 'approved' ? 'success' : 'warning'}>{row.grnStatus?.toUpperCase()}</Badge>} 
            />
            {row.storageLocations && (
              <DetailRow 
                label="Storage Locations" 
                value={
                  <div className="space-y-1">
                    {row.storageLocations.split(' | ').map((loc: string, idx: number) => (
                      <div key={idx} className="px-2 py-1 bg-brand-50 text-brand-700 rounded text-sm">
                        {loc}
                      </div>
                    ))}
                  </div>
                } 
              />
            )}
            <DetailRow label="Created At" value={formatDateTimeWithSeconds(row.createdAt)} />
          </>
        );

      case 'outward':
        return (
          <>
            <DetailRow label="Dispatch Number" value={row.dispatchNumber} />
            <DetailRow label="Client" value={row.clientName} />
            <DetailRow label="Warehouse" value={row.warehouseName} />
            <DetailRow
              label="LR Number"
              value={row.lrNumber ? <span className="font-mono font-semibold text-gray-900">{row.lrNumber}</span> : '-'}
            />
            <DetailRow
              label="E-Waybill Number"
              value={row.eWaybillNumber ? <span className="font-mono font-semibold text-gray-900">{row.eWaybillNumber}</span> : '-'}
            />
            <DetailRow label="Product" value={row.productDescription} />
            <DetailRow label="Batch No" value={row.batchNo} />
            <DetailRow label="Handling Type" value={row.handlingType ? row.handlingType.toUpperCase() : '-'} />
            <DetailRow label="Good Quantity" value={row.goodQty?.toLocaleString() || '0'} />
            <DetailRow label="CDAM Quantity" value={row.cdamQty?.toLocaleString() || '0'} />
            <DetailRow label="DAM Quantity" value={row.damQty?.toLocaleString() || '0'} />
            <DetailRow label="Total Pallets" value={row.palletCount?.toLocaleString() || '0'} />
            <DetailRow
              label="Status"
              value={<Badge variant={row.dispatchStatus === 'approved' ? 'success' : 'warning'}>{row.dispatchStatus?.toUpperCase()}</Badge>}
            />
            {row.destination && <DetailRow label="Destination" value={row.destination} />}
            {row.dispatchRemarks && <DetailRow label="Dispatch Remarks" value={row.dispatchRemarks} />}
            {row.storageLocations && (
              <DetailRow
                label="Storage Locations"
                value={
                  <div className="space-y-1">
                    {row.storageLocations.split(' | ').map((loc: string, idx: number) => (
                      <div key={idx} className="px-2 py-1 bg-brand-50 text-brand-700 rounded text-sm">
                        {loc}
                      </div>
                    ))}
                  </div>
                }
              />
            )}
            <DetailRow label="Created At" value={formatDateTimeWithSeconds(row.createdAt)} />
          </>
        );

      case 'damage':
        return (
          <>
            <DetailRow label="Client" value={row.clientName} />
            <DetailRow label="Product" value={row.productDescription} />
            <DetailRow label="Batch No" value={row.batchNo} />
            <DetailRow label="DAM Quantity" value={row.damQty?.toLocaleString() || '0'} />
            <DetailRow label="CDAM Quantity" value={row.cdamQty?.toLocaleString() || '0'} />
            <DetailRow label="Total Damaged" value={row.totalDamaged?.toLocaleString() || '0'} />
            {row.lbdStorageMode && (
              <>
                <DetailRow
                  label="LBD Storage Type"
                  value={
                    <Badge variant={row.lbdStorageMode === 'shared' ? 'warning' : 'info'}>
                      {row.lbdStorageMode === 'shared' ? 'SHARED PALLET' : 'DEDICATED'}
                    </Badge>
                  }
                />
                <DetailRow label="LBD Zone / Pallet" value={row.lbdZoneCodes || '-'} />
                {row.lbdStorageMode === 'dedicated' && (
                  <DetailRow
                    label="LBD Pallet Count"
                    value={row.lbdPalletCount?.toLocaleString() || '0'}
                  />
                )}
                {row.lbdStorageMode === 'shared' && (
                  <DetailRow label="Shared Pallet Ref" value={row.lbdSharedPalletRef || '-'} />
                )}
                {row.lbdRemark && <DetailRow label="LBD Remark" value={row.lbdRemark} />}
              </>
            )}
            <DetailRow label="Updated At" value={formatDateTimeWithSeconds(row.updatedAt)} />
          </>
        );

      case 'client-utilization':
        const utilizationColor = row.utilization >= 90 ? 'bg-red-500' : row.utilization >= 70 ? 'bg-yellow-500' : 'bg-green-500';
        const palletsRemaining = row.palletsRemaining || 0;
        const additionalPalletsUsed = row.additionalPalletsUsed || 0;
        const additionalSpaceSqft = row.additionalSpaceSqft || 0;
        const isPerPallet = isPerPalletClient(row.clientType);
        
        return (
          <>
            <DetailRow label="Client Name" value={row.clientName} />
            <DetailRow 
              label="Client Type" 
              value={<Badge variant="info">{row.clientType?.replace('_', ' ').toUpperCase()}</Badge>} 
            />
            <DetailRow 
              label={isPerPallet ? "Base Pallets Allocated" : "Base Space Allocated"} 
              value={isPerPallet ? `${row.palletsAllocated?.toLocaleString() || '0'} pallets` : `${((row.palletsAllocated ?? 0) * 25).toLocaleString()} sqft`}
            />
            <DetailRow 
              label={isPerPallet ? "Base Pallets Used" : "Base Space Used"} 
              value={isPerPallet ? `${Math.min(row.palletsUsed || 0, row.palletsAllocated || 0).toLocaleString()} pallets` : `${(Math.min(row.palletsUsed || 0, row.palletsAllocated || 0) * 25).toLocaleString()} sqft`}
            />
            <DetailRow 
              label={isPerPallet ? "Base Pallets Remaining" : "Base Space Remaining"} 
              value={
                <span className="text-green-600 font-semibold">
                  {isPerPallet ? `${palletsRemaining.toLocaleString()} pallets` : `${((palletsRemaining ?? 0) * 25).toLocaleString()} sqft`}
                </span>
              } 
            />
            
            {additionalSpaceSqft > 0 && (
              <DetailRow 
                label="Additional Space Allocated" 
                value={
                  <span className="text-brand-600 font-semibold">
                    {additionalSpaceSqft.toLocaleString()} sqft
                  </span>
                } 
              />
            )}
            
            {additionalPalletsUsed > 0 && (
              <DetailRow 
                label="Additional Pallets Used" 
                value={
                  <div className="flex items-center">
                    <span className="text-orange-600 font-bold">
                      {additionalPalletsUsed.toLocaleString()} pallets
                    </span>
                    <Badge variant="warning" className="ml-2">Using Additional Space</Badge>
                  </div>
                } 
              />
            )}
            
            <DetailRow 
              label="Utilization" 
              value={
                <div className="flex items-center space-x-2">
                  <div className="flex-1 bg-gray-200 rounded-full h-2">
                    <div
                      className={`${utilizationColor} h-2 rounded-full transition-all duration-300`}
                      style={{ width: `${Math.min(row.utilization || 0, 100)}%` }}
                    />
                  </div>
                  <span className="font-medium">{row.utilization?.toFixed(1) || 0}%</span>
                </div>
              } 
            />
          </>
        );

      case 'additional-space':
        const isPerPalletType = isPerPalletClient(row.clientType);
        const spaceSqft = Number(row.spaceSqft || 0);
        const palletCount = Number(row.palletCount || 0);
        const durationDays = Number(row.durationDays || 0);
        
        return (
          <>
            <DetailRow label="Client Name" value={row.clientName} />
            <DetailRow 
              label="Client Type" 
              value={<Badge variant="info">{row.clientType?.replace('_', ' ').toUpperCase()}</Badge>} 
            />
            <DetailRow 
              label="Space/Pallets Used" 
              value={
                <span className="font-bold text-orange-600">
                  {isPerPalletType 
                    ? `${palletCount.toLocaleString()} pallets` 
                    : `${spaceSqft.toLocaleString()} sqft`
                  }
                </span>
              } 
            />
            <DetailRow 
              label="Start Date" 
              value={row.startDate ? formatDateTimeWithSeconds(row.startDate) : '-'} 
            />
            <DetailRow 
              label="End Date" 
              value={
                row.isActive ? (
                  <Badge variant="success">Currently Active</Badge>
                ) : (
                  row.endDate ? formatDateTimeWithSeconds(row.endDate) : '-'
                )
              } 
            />
            <DetailRow 
              label="Duration" 
              value={
                <span className="font-semibold">
                  {durationDays} {durationDays === 1 ? 'day' : 'days'}
                </span>
              } 
            />
            <DetailRow 
              label="Status" 
              value={
                row.isActive ? (
                  <Badge variant="success">Active</Badge>
                ) : (
                  <Badge variant="secondary">Released</Badge>
                )
              } 
            />
            {row.grnNumber && (
              <DetailRow 
                label="GRN Number" 
                value={
                  <span className="font-mono text-sm">{row.grnNumber}</span>
                } 
              />
            )}
            <div className="mt-4 p-4 bg-brand-50 rounded-lg border border-brand-200">
              <p className="text-sm text-brand-800">
                <strong>Billing Information:</strong> This client used additional space for {durationDays} {durationDays === 1 ? 'day' : 'days'}.
                {row.isActive && ' The usage is currently active and ongoing.'}
              </p>
            </div>
          </>
        );

      case 'billing':
        return (
          <>
            <DetailRow label="Invoice Number" value={row.invoiceNumber} />
            <DetailRow label="Client" value={row.clientName} />
            <DetailRow label="Warehouse" value={row.warehouseName} />
            <DetailRow label="Invoice Status" value={<Badge variant="secondary">{row.invoiceStatus}</Badge>} />
            <DetailRow label="Billing Period" value={`${row.billingPeriodStart || '-'} to ${row.billingPeriodEnd || '-'}`} />
            <DetailRow label="Charge Type" value={(row.chargeType || '').replace(/_/g, ' ')} />
            <DetailRow label="Description" value={row.description} />
            <DetailRow label="Quantity" value={`${row.quantity ?? 0} ${row.unit || ''}`} />
            <DetailRow label="Rate" value={`₹${(row.rate ?? 0).toLocaleString('en-IN')}`} />
            <DetailRow label="Amount" value={`₹${(row.amount ?? 0).toLocaleString('en-IN')}`} />
            <DetailRow label="Invoice Total" value={`₹${(row.invoiceTotalAmount ?? 0).toLocaleString('en-IN')}`} />
            {row.invoiceCreatedAt && (
              <DetailRow label="Invoice Date" value={formatDateTimeWithSeconds(row.invoiceCreatedAt)} />
            )}
          </>
        );

      default:
        return <p className="text-gray-500">No details available</p>;
    }
  };

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40"
        onClick={onClose}
      />

      {/* Drawer - bottom sheet below lg, right-side drawer on desktop */}
      <div className="fixed bottom-0 inset-x-0 max-h-[85vh] w-full rounded-t-2xl animate-slide-up lg:animate-none lg:rounded-none lg:left-auto lg:top-0 lg:max-h-full lg:h-full lg:w-1/3 bg-white shadow-xl z-50 overflow-y-auto overflow-x-hidden">
        {/* Grab handle - mobile bottom sheet only */}
        <div className="lg:hidden sticky top-0 bg-white pt-3 pb-1 flex justify-center rounded-t-2xl">
          <div className="h-1.5 w-10 rounded-full bg-gray-300"></div>
        </div>
        <div className="px-4 pb-4 pt-1 lg:p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-4 lg:mb-6">
            <h2 className="text-lg lg:text-2xl font-bold text-gray-900">Record Details</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors active:scale-[0.95]"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>

          {/* Details */}
          <div className="space-y-4">
            {renderDetails()}
          </div>
        </div>
      </div>
    </>
  );
}

function DetailRow({ label, value }: { label: string; value: any }) {
  return (
    <div className="border-b border-gray-100 pb-3 min-w-0">
      <p className="text-xs lg:text-sm text-gray-600 mb-1">{label}</p>
      <div className="text-sm lg:text-base font-medium text-gray-900 break-words overflow-hidden">{value || '-'}</div>
    </div>
  );
}
